import {Injectable} from '@angular/core';
import {BehaviorSubject, finalize, map, Observable, shareReplay} from "rxjs";
import {IdentityUser} from "@app/_models/identityUser";
import {jwtDecode, JwtPayload} from "jwt-decode";
import {ForgotPasswordRequest, LoginResponse, NewPasswordRequest, ResetPasswordRequest} from "../_models/api";
import {environment} from "@environments/environment";
import {HttpClient} from "@angular/common/http";
import {Router} from "@angular/router";

interface VMJwtIdPayload extends JwtPayload {
    name: string;
    email: string;
    "custom:customerid": string;
    "cognito:groups": string[];
}

@Injectable({
    providedIn: 'root'
})
export class AuthService {
    private _userSubject: BehaviorSubject<IdentityUser | null>;
    private _loggedIn = new BehaviorSubject<boolean>(false);
    private _isAdmin = new BehaviorSubject<boolean>(false);
    private _roles = new BehaviorSubject<string[] | null>(null);
    private refreshObservable: Observable<boolean> | null = null;
    private eventTypes = { loginEvent: "loginEvent", refreshEvent: "refreshEvent" };

    constructor(private router: Router, private http: HttpClient) {
        let user: IdentityUser | null = JSON.parse(localStorage.getItem("user")!);
        this._userSubject = new BehaviorSubject(user);

        if (this._userSubject.value == null) {
            this.resetBehaviorSubjects();
            return;
        }

        this.setBehaviorSubjects(user!);
    }

    get userValue() {
        return this._userSubject.value;
    }

    user(): Observable<IdentityUser | null> {
        return this._userSubject.asObservable();
    }

    isLoggedIn(): Observable<boolean> {
        return this._loggedIn.asObservable();
    }

    login(email: string, password: string): Observable<LoginResponse> {
        const endpoint = `${environment.apiBaseUrl}/v3/admin/login`;
        return this.http.post<LoginResponse>(endpoint, {email, password});
    }

    newPasswordChallenge(request: NewPasswordRequest): Observable<LoginResponse> {
        const endpoint = `${environment.apiUrl}/password-auth-challenge`;
        return this.http.post<LoginResponse>(endpoint, request);
    }

    forgotPassword(request: ForgotPasswordRequest): Observable<any> {
        const endpoint = `${environment.apiUrl}/forgot-password`;
        return this.http.post<any>(endpoint, request);
    }

    resetPassword(request: ResetPasswordRequest): Observable<any> {
        const endpoint = `${environment.apiUrl}/reset-password`;
        return this.http.post<any>(endpoint, request);
    }

    refreshToken(email: string): Observable<boolean> {
        // Concurrent 401s must share a single refresh POST — Cognito rotates the refresh-token cookie
        // on success, so a second POST would 401 and the interceptor would log the user out.
        if (this.refreshObservable) {
            return this.refreshObservable;
        }

        this.refreshObservable = this.http
            .post<LoginResponse>(`${environment.apiBaseUrl}/v3/admin/refresh-token`, {email: email})
            .pipe(
                map((response: LoginResponse) => {
                    const user = this.decodeTokens(response.success!.idToken);
                    localStorage.setItem('user', JSON.stringify(user));
                    this.setBehaviorSubjects(user, this.eventTypes.refreshEvent);
                    return true;
                }),
                shareReplay(1),
                finalize(() => {
                    this.refreshObservable = null;
                })
            );

        return this.refreshObservable;
    }

    signout(): Observable<any> {
        return this.http.post<any>(`${environment.apiBaseUrl}/v3/admin/signout`, {});
    }

    logout(queryParams?: object) {
        localStorage.removeItem("user");

        // eventually we can remove this code since we are no longer using session storage
        sessionStorage.removeItem("accessToken");
        sessionStorage.removeItem("refreshToken");
        sessionStorage.removeItem("idToken");
        sessionStorage.removeItem("user");

        this.resetBehaviorSubjects();
        this.router.navigate(['/account/login'], queryParams).then();
    }

    handleSuccessfulLogin(response: LoginResponse): IdentityUser {
        const user = this.decodeTokens(response.success!.idToken);
        localStorage.setItem('user', JSON.stringify(user));
        this.setBehaviorSubjects(user);

        return user;
    }

    private decodeTokens(idToken: string): IdentityUser {
        if (idToken == null) {
            throw new Error("Authentication Error.");
        }

        const idTokenData = jwtDecode<VMJwtIdPayload>(idToken);
        return {
            name: idTokenData.name,
            email: idTokenData.email,
            customerId: idTokenData["custom:customerid"],
            roles: idTokenData["cognito:groups"],
            idToken: idToken,
        };
    }

    private resetBehaviorSubjects() {
        this._userSubject.next(null);
        this._loggedIn.next(false);
        this._isAdmin.next(false);
        this._roles.next(null);
    }

    private setBehaviorSubjects(user: IdentityUser, eventType: string = this.eventTypes.loginEvent) {
        this._userSubject.next(user);
        this._isAdmin.next(this._userSubject.value!.roles!.includes("Admin"));
        this._roles.next(this._userSubject.value!.roles!);

        if (eventType === this.eventTypes.loginEvent) {
            this._loggedIn.next(true);
        }
    }
}
